import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import take from 'lodash-es/take';
import throttle from 'lodash-es/throttle';
import { Subscription } from 'rxjs/internal/Subscription';
import { RadioComponent } from '../../ui/form/radio';
import { CheckoutDeliveryType, CheckoutFormComponent } from '../checkout-form.component';
import { CheckoutStepComponent } from '../checkout-step.component';

@Component({
    selector: 'checkout-delivery-selection',
    templateUrl: './checkout-delivery-selection.component.html',
    standalone: true,
    imports: [CommonModule, FormsModule, RadioComponent, RadioGroupDirective, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutDeliverySelectionComponent implements OnInit, OnDestroy {
    customRulesForDisabledDelivery: number[] = [139, 145];
    forbiddenCountries: string[] = ['GG', 'JE', 'IE', 'IM'];
    methodsLabel?: string;

    private subscriptions = new Subscription();

    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
    }

    protected select = throttle(
        (type: CheckoutDeliveryType) => {
            this.checkoutFormComponent.selectDeliveryType(type);
            this.detectChanges();
        },
        100,
        { trailing: true, leading: false }
    );

    constructor(
        protected checkoutFormComponent: CheckoutFormComponent,
        protected checkoutStepComponent: CheckoutStepComponent,
        protected app: App,
        protected changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.ShippingMethodsUpdate).subscribe(() => this.onMethodsUpdate()));
        this.onMethodsUpdate();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.checkoutStepComponent.removeForm(this._stepForm);
    }

    get hasCollectionMethods(): boolean {
        return this.checkoutFormComponent.hasCollectionMethods;
    }

    get hasNonCollectionMethods(): boolean {
        return (
            this.checkoutFormComponent.hasNonCollectionMethods ||
            this.checkoutFormComponent.data.address.shipping.country_id !== this.app.multisite.current.country
        );
    }

    get deliveryType(): CheckoutDeliveryType | undefined {
        return this.checkoutFormComponent.deliveryTypeSelected;
    }

    get showDeliveryError(): boolean {
        const customRules = this.app.customer.basket.data.custom_rules || [];
        return this.customRulesForDisabledDelivery.some(number => customRules.includes(number));
    }

    selectShipping(): void {
        this.select(CheckoutDeliveryType.shipping);
    }

    selectCollection(): void {
        this.select(CheckoutDeliveryType.collection);
    }

    protected onMethodsUpdate(): void {
        const methods = take(this.checkoutFormComponent.nonCollectionShippingMethods, 2);

        this.methodsLabel = methods.map(i => i.title).join(', ');
        this.detectChanges();
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
